import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import axios from '../axios';
import { loginBranchAPI } from '../api-services/Login.service';
import { API_NETWORK_ERROR, localStorageKeys } from '../../utils/constants';
import responseErrorMapping from '../../utils/error.response.mapping';

export const BranchLoginContext = createContext(null);

const initialLoginResponse = {
  loading: false,
  success: null,
  message: null,
  data: null,
};

/**
 * Normalizes LoginBranch success body into the object stored as `loginInfo`
 * (must include `token` for defaultConstants / axios Bearer).
 */
function buildLoginInfoFromSuccessBody(body) {
  if (!body || typeof body !== 'object') return {};
  const token = body.token ?? body.access_token;
  const nested = body.data != null && typeof body.data === 'object' ? body.data : {};
  const refresh =
    body.refresh_token ?? body.refreshToken ?? nested.refresh_token ?? nested.refreshToken;
  const tokenResolved = token ?? nested.token ?? nested.access_token;
  return {
    ...nested,
    token: tokenResolved,
    refresh_token: refresh,
    temporary_password: body.temporary_password === true || body.temporary_password === 'true',
    CustomMessage: body.CustomMessage,
  };
}

function normalizeInterceptorMessage(message) {
  if (message == null) return '';
  if (typeof message === 'string') return message;
  if (typeof message === 'object' && message.CustomMessage) return String(message.CustomMessage);
  return String(message);
}

export function BranchLoginProvider({ children }) {
  const [loginAPIResponse, setLoginAPIResponse] = useState(initialLoginResponse);

  const persistLoginSession = useCallback((serverBody) => {
    const { TOKEN, REFRESH_TOKEN, LOGIN_INFO } = localStorageKeys;
    const loginInfo = buildLoginInfoFromSuccessBody(serverBody);
    if (!loginInfo.token) {
      console.warn('LoginBranch: success response missing token');
    }
    localStorage.setItem(LOGIN_INFO, JSON.stringify(loginInfo));
    if (loginInfo.token) {
      localStorage.setItem(TOKEN, loginInfo.token);
      axios.defaults.headers.common.Authorization = `Bearer ${loginInfo.token}`;
    }
    if (loginInfo.refresh_token) {
      localStorage.setItem(REFRESH_TOKEN, loginInfo.refresh_token);
    }
  }, []);

  const onLogin = useCallback(
    async (payload) => {
      try {
        setLoginAPIResponse({
          loading: true,
          success: null,
          message: null,
          data: null,
        });

        const res = await loginBranchAPI(payload);

        if (res?.hasError) {
          const raw =
            (typeof res.message === 'string' && res.message.trim()) ||
            normalizeInterceptorMessage(res.message) ||
            (typeof res.error?.message === 'string' && res.error.message.trim()) ||
            '';
          const mapped = raw ? responseErrorMapping(raw) : API_NETWORK_ERROR;
          setLoginAPIResponse({
            loading: false,
            data: null,
            success: false,
            message: mapped,
          });
          return { success: false, userMessage: mapped };
        }

        const body = res?.data ?? {};
        const { fetchApiTime: _t, ...serverBody } = body;

        if (serverBody.success === true) {
          persistLoginSession(serverBody);
          setLoginAPIResponse({
            loading: false,
            data: res,
            success: true,
            message: serverBody.CustomMessage ?? null,
          });
          return { success: true, userMessage: null };
        }

        const failMsgRaw =
          serverBody.errors?.CustomMessage ??
          serverBody.errors?.message ??
          serverBody.message ??
          serverBody.CustomMessage ??
          '';

        const authFailMessage = failMsgRaw
          ? responseErrorMapping(String(failMsgRaw))
          : 'Something went wrong';

        setLoginAPIResponse({
          loading: false,
          data: null,
          success: serverBody.success === false ? false : null,
          message: failMsgRaw || null,
        });
        return { success: false, userMessage: authFailMessage };
      } catch (error) {
        console.log('BranchLoginProvider onLogin catch', error);
        const msg = error?.message || 'Something went wrong';
        setLoginAPIResponse({
          loading: false,
          data: null,
          success: false,
          message: msg,
        });
        return { success: false, userMessage: msg };
      }
    },
    [persistLoginSession]
  );

  const value = useMemo(
    () => ({
      loginAPIResponse,
      setLoginAPIResponse,
      onLogin,
    }),
    [loginAPIResponse, onLogin]
  );

  return <BranchLoginContext.Provider value={value}>{children}</BranchLoginContext.Provider>;
}

export function useBranchLogin() {
  const ctx = useContext(BranchLoginContext);
  if (!ctx) {
    throw new Error('useBranchLogin must be used within BranchLoginProvider');
  }
  return ctx;
}
